import React, { useRef, useEffect } from "react";
import { EditorTab } from "./types";
import { useTabStacking } from "./useTabStacking";
import { BrowserTab } from "./BrowserTab";

let draggedElement: Element | null = null;

interface EditorTabBarProps {
  tabs: EditorTab[];
  activeIndex: number;
  tabsetId?: string;
  onSelectTab: (index: number) => void;
  onCloseTab: (index: number) => void;
  onReorderTabs: (oldIndex: number, newIndex: number) => void;
  leftIcon?: React.ReactNode;
  rightToolbar?: React.ReactNode;
  // External drag preview system (floating ghost + compass for moving tabs between panels)
  onExternalDragStart?: (e: React.DragEvent, index: number) => void;
  onExternalDragOver?: (clientX: number, clientY: number) => void;
}

export const EditorTabBar: React.FC<EditorTabBarProps> = ({
  tabs,
  activeIndex,
  tabsetId,
  onSelectTab,
  onCloseTab,
  onReorderTabs,
  leftIcon,
  rightToolbar,
  onExternalDragStart,
  onExternalDragOver,
}) => {
  const dragIndexRef = useRef<number | null>(null);
  const mainRef = useRef<HTMLDivElement>(null);

  const {
    containerRef,
    scrollRef,
    tabW,
    totalW,
    positions,
    drawOrder,
    config,
  } = useTabStacking(tabs.length, activeIndex);

  // Soft smooth wheel scrolling with momentum animation
  const scrollTargetRef = useRef<number>(0);
  const scrollAnimRef = useRef<number | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    scrollTargetRef.current = el.scrollLeft;

    const animateScroll = () => {
      if (!scrollRef.current) return;
      const current = scrollRef.current.scrollLeft;
      const target = scrollTargetRef.current;
      const diff = target - current;

      if (Math.abs(diff) > 0.5) {
        scrollRef.current.scrollLeft += diff * 0.25; // Soft deceleration factor
        scrollAnimRef.current = requestAnimationFrame(animateScroll);
      } else {
        scrollRef.current.scrollLeft = target;
        scrollAnimRef.current = null;
      }
    };

    const handleNativeWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return;

      const isHorizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      const delta = isHorizontal ? e.deltaX : e.deltaY;

      if (delta !== 0) {
        e.preventDefault();
        const maxScroll = el.scrollWidth - el.clientWidth;
        const currentTarget = scrollAnimRef.current !== null ? scrollTargetRef.current : el.scrollLeft;
        scrollTargetRef.current = Math.max(0, Math.min(currentTarget + delta * 0.85, maxScroll));

        if (scrollAnimRef.current === null) {
          scrollAnimRef.current = requestAnimationFrame(animateScroll);
        }
      }
    };

    el.addEventListener("wheel", handleNativeWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", handleNativeWheel);
      if (scrollAnimRef.current !== null) cancelAnimationFrame(scrollAnimRef.current);
    };
  }, [scrollRef]);

  // Dragstart/dragover are handled by native listeners in dockable-layout that look for data-editor-tab-index
  // No need to add listeners here since the wrapper's native listeners will capture the events

  // Drag and Drop handlers — both internal reordering AND external preview
  const handleDragStart = (e: React.DragEvent, index: number) => {
    dragIndexRef.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", `${index}`);
    // Also trigger external drag-preview system (floating ghost for moving between panels)
    onExternalDragStart?.(e, index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    const sourceIndex = dragIndexRef.current;
    if (sourceIndex !== null && sourceIndex !== targetIndex) {
      onReorderTabs(sourceIndex, targetIndex);
    }
    dragIndexRef.current = null;
  };

  const shouldShowClose = (tabW: number) => {
    return tabW >= config.minTabWidth;
  };

  return (
    <div
      ref={mainRef}
      className="relative flex items-center h-[52px] -mb-[3px] z-10 text-slate-900 select-none overflow-visible w-full bg-transparent"
    >
      {/* Row layout */}
      <div className="flex items-center w-full h-[52px]">
        {/* Left icon button (previous TabListPopover component) */}
        {leftIcon && (
          <div className="relative z-30 shrink-0 pl-0 pr-1 flex items-center h-[52px]">
            {leftIcon}
          </div>
        )}

        {/* Scrollable Tab Strip with overflow-x-auto and hidden scrollbars */}
        <div ref={containerRef} className="relative flex-1 h-[52px] overflow-visible z-10 min-w-0">
          <div
            ref={scrollRef}
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
            className="w-full h-[52px] overflow-x-scroll overflow-y-hidden no-scrollbar"
          >
            <div
              style={{ width: `${totalW}px`, height: "52px", minHeight: "100%" }}
              className="relative"
            >
              {drawOrder.map((i, orderRank) => {
                const pos = positions[i];
                if (!pos || pos.isHidden) return null;

                // Explicit zIndex based on draw order position (orderRank + 1)
                const zIndex = orderRank + 1;

                return (
                  <BrowserTab
                    key={tabs[i]?.id || i}
                    tab={tabs[i]}
                    index={i}
                    isActive={i === activeIndex}
                    tabWidth={tabW}
                    showClose={shouldShowClose(tabW)}
                    pos={pos}
                    zIndex={zIndex}
                    tabsetId={tabsetId}
                    onSelect={onSelectTab}
                    onClose={onCloseTab}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* Right toolbar (e.g. + add tab button or toolbar controls) */}
        {rightToolbar && (
          <div className="relative z-30 shrink-0 pr-2 flex items-center h-[52px]">
            {rightToolbar}
          </div>
        )}
      </div>
    </div>
  );
};
